// Reparto de ventas y monto por categoría de producto, para la ficha de Tiendas.
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { lineNetCents, soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import type { ProductCategory } from '@/domains/sales/wizard/types';
import { CATEGORY_LABEL } from '@/domains/sales/wizard/cartLines';
import { SERVICE_CATEGORY } from '@/shared/lib/format/productCategory';
import {
    lineCategoryKey,
    lineCategoryLabel,
    UNCATEGORIZED_LINE,
    type LineCategoryKey,
} from '@/shared/patterns/SaleTicket/ticketSummary';

export const UNCATEGORIZED = UNCATEGORIZED_LINE;

export type CategoryMixKey = LineCategoryKey;

export interface CategoryMixSlice {
    category: CategoryMixKey;
    label: string;
    /** Ventas que tienen al menos una línea de la categoría (o piezas, con `unit: 'piece'`). */
    salesCount: number;
    amountCents: number;
    unit?: 'sale' | 'piece';
}

type MixSale = Pick<
    BackofficeSale,
    'sku' | 'lines' | 'equipmentPriceCents' | 'amountCents' | 'cancelledAt'
> &
    Partial<Pick<BackofficeSale, 'soldCents' | 'type'>>;

function isKnownCategory(category: string | null): category is CategoryMixKey {
    return category !== null && (category in CATEGORY_LABEL || category === SERVICE_CATEGORY);
}

function sortSlices(slices: CategoryMixSlice[]): CategoryMixSlice[] {
    return slices.sort((a, b) => {
        if (a.category === UNCATEGORIZED) return 1;
        if (b.category === UNCATEGORIZED) return -1;
        return a.label.localeCompare(b.label, 'es-MX');
    });
}

/** El mix que ya calculó `GET /backoffice/stores/{id}/detail`, con las etiquetas de la pantalla. */
export function categoryMixFromDetail(
    mix: readonly { category: string | null; units: number; placedCents: number }[],
): CategoryMixSlice[] {
    const merged = new Map<CategoryMixKey, CategoryMixSlice>();
    for (const slice of mix) {
        const category = isKnownCategory(slice.category) ? slice.category : UNCATEGORIZED;
        const slot = merged.get(category) ?? {
            category,
            label: lineCategoryLabel(category),
            salesCount: 0,
            amountCents: 0,
            unit: 'piece' as const,
        };
        slot.salesCount += slice.units;
        slot.amountCents += slice.placedCents;
        merged.set(category, slot);
    }
    return sortSlices([...merged.values()]);
}

function hasLines(sale: MixSale): boolean {
    return sale.lines !== undefined && sale.lines.length > 0;
}

/** SKUs de las ventas anteriores a las líneas, cuya categoría hay que buscar en el catálogo. */
export function legacySaleSkus(sales: MixSale[]): string[] {
    const skus = new Set(
        sales
            .filter((sale) => !hasLines(sale))
            .map((sale) => sale.sku)
            .filter((sku): sku is string => sku !== null),
    );
    return [...skus].sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Categoría de cada SKU pedido según el catálogo; el que no aparece se queda sin entrada. */
export function categoriesBySku(
    catalog: { sku: string; category: ProductCategory }[],
    skus: string[],
): Map<string, ProductCategory> {
    const wanted = new Set(skus);
    return new Map(
        catalog.filter((item) => wanted.has(item.sku)).map((item) => [item.sku, item.category]),
    );
}

/** Categoría de la venta sin líneas según su SKU; sin teléfono no hay SKU que buscar. */
function legacyCategoryOf(
    sale: MixSale,
    legacyCategories: ReadonlyMap<string, ProductCategory>,
): CategoryMixKey {
    const category = sale.sku === null ? undefined : legacyCategories.get(sale.sku);
    return category ?? UNCATEGORIZED;
}

function isCountable(sale: MixSale): boolean {
    return sale.cancelledAt === null && sale.type !== 'EXTERNAL_REFERRAL';
}

/** Categorías de la venta (sin regalos); sin líneas, la de su SKU en el catálogo. */
export function saleCategoryKeys(
    sale: MixSale,
    legacyCategories: ReadonlyMap<string, ProductCategory>,
): CategoryMixKey[] {
    if (!hasLines(sale)) return [legacyCategoryOf(sale, legacyCategories)];
    const keys = (sale.lines ?? []).filter((line) => line.role !== 'GIFT').map(lineCategoryKey);
    return [...new Set(keys)];
}

/**
 * Suma el neto de cada línea en su categoría; la venta sin líneas va entera a la de su SKU. Las
 * canceladas, los regalos y las derivaciones externas (vendido 0, DEC-46) no cuentan. Orden alfabético.
 */
export function buildCategoryMix(
    sales: MixSale[],
    legacyCategories: ReadonlyMap<string, ProductCategory>,
): CategoryMixSlice[] {
    const totals = new Map<CategoryMixKey, { salesCount: number; amountCents: number }>();
    const add = (category: CategoryMixKey, cents: number, countSale: boolean) => {
        const slot = totals.get(category) ?? { salesCount: 0, amountCents: 0 };
        slot.amountCents += cents;
        if (countSale) slot.salesCount += 1;
        totals.set(category, slot);
    };

    for (const sale of sales) {
        if (!isCountable(sale)) continue;
        if (!hasLines(sale)) {
            add(legacyCategoryOf(sale, legacyCategories), soldCentsOf(sale), true);
            continue;
        }
        const counted = new Set<CategoryMixKey>();
        for (const line of sale.lines ?? []) {
            // El regalo vale $0 y no es venta: no aparece en el mix.
            if (line.role === 'GIFT') continue;
            const category = lineCategoryKey(line);
            add(category, lineNetCents(line), !counted.has(category));
            counted.add(category);
        }
    }

    return sortSlices(
        [...totals.entries()].map(([category, slot]) => ({
            category,
            label: lineCategoryLabel(category),
            ...slot,
        })),
    );
}
